import type { ComboboxRootChangeEventDetails } from "@base-ui/react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import { useState } from "react";
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from "@/components/ui/combobox";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/utils/customHook.util";
import { AiCapturedBadge } from "../ai-capture/AiCapturedBadge";
import { type LocationSuggestion, useLocationSearch } from "./LocationSearchContext";

export type LocationOption = {
    city: string;
    country: string;
    countryCode: string;
};

const SEARCH_DEBOUNCE_MS = 250;
const EMPTY_SUGGESTIONS: LocationSuggestion[] = [];

export type LocationComboboxProps = {
    id?: string;
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
    onPlaceChange?: (place: LocationOption) => void;
    placeholder?: string;
    aiCaptured?: boolean;
};

export function LocationCombobox({
    id,
    value,
    defaultValue,
    onValueChange,
    onPlaceChange,
    placeholder = "Search by city",
    aiCaptured = false,
}: LocationComboboxProps) {
    const { searchLocation, resolveLocation } = useLocationSearch();
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS).trim();

    const { data: fetchedSuggestions = EMPTY_SUGGESTIONS } = useQuery({
        queryKey: ["location-search", debouncedSearchTerm],
        queryFn: () => searchLocation(debouncedSearchTerm),
        enabled: debouncedSearchTerm.length > 0,
        placeholderData: keepPreviousData,
    });
    const suggestions =
        debouncedSearchTerm.length > 0 ? fetchedSuggestions : EMPTY_SUGGESTIONS;
    const items = suggestions.map((suggestion) => suggestion.placeId);

    function handleInputChange(
        next: string,
        eventDetails: ComboboxRootChangeEventDetails,
    ) {
        if (eventDetails.reason === "input-change") {
            setSearchTerm(next);
        }
    }

    async function handleSelect(placeId: string) {
        const suggestion = suggestions.find((item) => item.placeId === placeId);
        const label = suggestion?.label ?? placeId;
        onValueChange?.(label);

        const place = await resolveLocation(placeId);
        onPlaceChange?.(place ?? { city: label, country: "", countryCode: "" });
    }

    return (
        <Combobox
            autoHighlight
            filter={null}
            items={items}
            defaultInputValue={value ?? defaultValue}
            itemToStringLabel={(placeId: string) => labelFor(suggestions, placeId)}
            onInputValueChange={handleInputChange}
            onValueChange={(next) => {
                if (typeof next === "string") void handleSelect(next);
            }}
        >
            <ComboboxInput
                id={id}
                placeholder={placeholder}
                showTrigger={!aiCaptured}
                className={cn("w-full", aiCaptured && "!border-blue-200 !bg-blue-50")}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                    }
                }}
            >
                {aiCaptured ? (
                    <AiCapturedBadge inline />
                ) : (
                    <MapPin className="size-4 text-muted-foreground" />
                )}
            </ComboboxInput>
            <ComboboxContent>
                <ComboboxEmpty>No cities match</ComboboxEmpty>
                <ComboboxList>
                    {(placeId: string) => (
                        <ComboboxItem key={placeId} value={placeId}>
                            {labelFor(suggestions, placeId)}
                        </ComboboxItem>
                    )}
                </ComboboxList>
            </ComboboxContent>
        </Combobox>
    );
}

function labelFor(suggestions: LocationSuggestion[], placeId: string): string {
    return suggestions.find((item) => item.placeId === placeId)?.label ?? placeId;
}
